import React, { useState, useMemo } from 'react'
import * as S from './styles'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ReactComponent as EditIcon } from 'presentation/assets/icons/pencil-new.svg'
import { ReactComponent as AlertCircleIcon } from 'presentation/assets/icons/alert-circle-grey.svg'
import { useHistory } from 'react-router'
import { useStores } from 'presentation/hooks/use-stores'
import { observer } from 'mobx-react'
import { Grid } from '@material-ui/core'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { Booleans } from 'common/enum/booleans'
import moment from 'moment-timezone'
import { hospitalizationList as hospitalizationTypeList } from 'common/enum/hospitalization-type'
import { hospitalizationList as hospitalizationModeList } from 'common/enum/hospitalization-mode'
import { techniquesList } from 'common/enum/techniques'
import DocumentAccordeon from 'presentation/shared/layouts/DocumentAccordeon'
import { DocumentsList } from 'common/enum/documents-list'
import { Document } from 'domain/entities/document'
import { OpenSurgicalOrderPatientDocument } from 'common/enum/patient-document'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { useServices } from 'presentation/hooks/use-services'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'
import { toast } from 'react-toastify'
import { ReportsCard } from 'presentation/shared/layouts/ReportsCard'
import { formatMask, phoneMaskWithParenthesis } from 'presentation/utils/masks'
import { normalizePhoneForMask } from 'presentation/utils/phone-utils'
import { OrderStepsPath } from 'presentation/contexts/surgical-order-v2/types'
import { filterByDataSource } from 'presentation/contexts/surgical-order-v2/utils/filterByDataSource'
import { maritalStatus } from 'presentation/utils/default-marital-status'
import { GenderNewTranslated } from 'common/enum/gender'
import { isPatientRN } from 'presentation/utils/validators/is-patient-rn'

type ReviewSurgicalOrderProps = {
  setShowReview: (value: boolean) => void
} & WithLoadingProps

const ReviewSurgicalOrder = WithLoading(
  observer(({ setIsLoading, setShowReview }: ReviewSurgicalOrderProps) => {
    const history = useHistory()
    const store = useStores().surgicalOrderV2
    const surgicalOrderService = useServices().surgicalOrder
    const [informationAccordionOpen, setInformationAccordionOpen] = useState('')

    const handleEditButton = (path: OrderStepsPath) => {
      setShowReview(false)
      history.push(path)
    }

    const booleansLabel = (value?: boolean | null) => {
      if (value === undefined || value === null) return '-'
      return Booleans[String(value) as keyof typeof Booleans]
    }

    const documentsNotToShow = [
      'LAUDO DE EXAME',
      'Laudos dos exames',
      'PLANEJAMENTO CIRÚRGICO',
      'Guia de autorização'
    ]

    const mappedDocuments = () => {
      return (
        store.surgicalOrder.documents?.map((document: Document) => ({
          type: document.type,
          status: document.status,
          documents: [
            {
              ...document,
              sended_at: document.sendedAt,
              status_updated_by: {
                name: document.updated_by?.name
              }
            }
          ]
        })) || []
      )
    }

    const documentsList = () => {
      const mappedDocs = mappedDocuments()

      return Object.keys(OpenSurgicalOrderPatientDocument).map((document) => {
        const docs = mappedDocs
          .filter((doc: any) => doc.type === document)
          .sort((a: any, b: any) => {
            if (moment(a.sendedAt).isAfter(b.sendedAt)) return -1
            else if (moment(a.sendedAt).isBefore(b.sendedAt)) return 1
            else return 0
          })

        const filesList = docs.reduce(
          (acc: any, curr: any) => [...acc, ...curr.documents],
          []
        )

        return {
          label: DocumentsList[document as keyof typeof DocumentsList],
          type: document,
          status: docs[0]?.status,
          documents: filesList
        }
      })
    }

    const filteredReports = store.surgicalOrder.documents?.filter(
      (document) =>
        document.type === OpenSurgicalOrderPatientDocument['Laudos dos exames']
    )

    const downloadDocument = async (
      document_id: number,
      _group_id: string,
      type: keyof typeof DocumentsList
    ) => {
      setIsLoading(true)
      try {
        const surgicalOrderId = store.surgicalOrder?.surgicalOrderId as number

        const file = await surgicalOrderService.loadSurgicalOrderDocument({
          surgical_order_id: surgicalOrderId,
          document_id
        })

        if (file) {
          downloadFileFromBlob(file.data, file.contentType, DocumentsList[type])
        }
      } catch {
        toast.error('Ocorreu um erro ao baixar o documento')
      } finally {
        setIsLoading(false)
      }
    }

    const handleChangeInformationAccordion = (
      label: string,
      isExpanded: boolean
    ) => {
      setInformationAccordionOpen(!isExpanded ? '' : label)
    }

    const procedureGroups = useMemo(
      () => filterByDataSource(store.procedures),
      [store.procedures]
    )
    const opmeGroups = useMemo(
      () => filterByDataSource(store.opmes),
      [store.opmes]
    )
    const providerGroups = useMemo(
      () => filterByDataSource(store.providers),
      [store.providers]
    )

    const isRN = isPatientRN(new Date(store.patient.birthDate))

    return (
      <S.Wrapper>
        {store.completedSteps.includes(0) ? (
          <S.CardInfo>
            <div className="c-header">
              <S.Title completed={store.completedSteps.includes(0)}>
                <span className="step">1</span>
                <h5 className="c-title">Informações cadastrais</h5>
              </S.Title>
              <ButtonNew
                className="btn-edit"
                icon={<EditIcon />}
                variant="text"
                fontSize="xsmall"
                noPadding
                onClick={() => handleEditButton(OrderStepsPath.REGISTRATION)}
              >
                Editar
              </ButtonNew>
            </div>
            <div className="c-content">
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Local da cirurgia</h6>
                </div>
                <div className="box-content">
                  <p className="text">
                    Unidade:{' '}
                    <b>{store.location.hospital.hospitalName || '-'}</b>
                  </p>
                  <p className="text">
                    Unidade cirúrgica:{' '}
                    <b>
                      {store.location.surgical_center.surgeryCenterName || '-'}
                    </b>
                  </p>
                </div>
              </S.BoxInfo>
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">
                    Dados do paciente {!!isRN && 'RN'}
                  </h6>
                </div>
                <div className="box-content">
                  <p className="text">
                    CPF: <b>{store.patient.cpf || '-'}</b>
                  </p>
                  <p className="text">
                    Nome: <b>{store.patient.name || '-'}</b>
                  </p>
                  <p className="text">
                    Celular:{' '}
                    <b>
                      {store.patient.phone
                        ? formatMask(
                            phoneMaskWithParenthesis,
                            normalizePhoneForMask(store.patient.phone)
                          )
                        : '-'}
                    </b>
                  </p>
                  <p className="text">
                    Email: <b>{store.patient.email || '-'}</b>
                  </p>
                  <p className="text">
                    Sexo:{' '}
                    <b>
                      {GenderNewTranslated[
                        store.patient.gender?.toUpperCase() as keyof typeof GenderNewTranslated
                      ] || '-'}
                    </b>
                  </p>
                  <p className="text">
                    Estado civil:{' '}
                    <b>
                      {maritalStatus.find(
                        (status) => status.value === store.patient.maritalStatus
                      )?.label || '-'}
                    </b>
                  </p>
                </div>
              </S.BoxInfo>
              {!!isRN && (
                <S.BoxInfo>
                  <div className="box-header">
                    <h6 className="box-title">Dados da mãe do paciente</h6>
                  </div>
                  <div className="box-content">
                    <p className="text">
                      CPF: <b>{store.patient.tutor?.cpf || '-'}</b>
                    </p>
                    <p className="text">
                      Nome: <b>{store.patient.tutor?.name || '-'}</b>
                    </p>
                    <p className="text">
                      Celular:{' '}
                      <b>
                        {store.patient.tutor?.phone
                          ? formatMask(
                              phoneMaskWithParenthesis,
                              normalizePhoneForMask(store.patient.tutor.phone)
                            )
                          : '-'}
                      </b>
                    </p>
                    <p className="text">
                      Email: <b>{store.patient.tutor?.email || '-'}</b>
                    </p>
                  </div>
                </S.BoxInfo>
              )}
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Dados do convênio</h6>
                </div>
                <div className="box-content">
                  <p className="text">
                    Convênio:{' '}
                    <b>{store.healthInsurance.healthInsuranceName || '-'}</b>
                  </p>
                  <p className="text">
                    Carteirinha:{' '}
                    <b>{store.healthInsurance.healthCard || '-'}</b>
                  </p>
                  <p className="text">
                    Validade:{' '}
                    <b>
                      {moment(store.healthInsurance.validThru).isValid()
                        ? moment(store.healthInsurance.validThru).format(
                            'DD/MM/YYYY'
                          )
                        : '-'}
                    </b>
                  </p>
                </div>
              </S.BoxInfo>
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Dados da cooperativa</h6>
                </div>
                <div className="box-content">
                  <p className="text">
                    Cooperativa: <b>{store.cooperative.cooperative || '-'}</b>
                  </p>
                </div>
              </S.BoxInfo>
            </div>
          </S.CardInfo>
        ) : (
          <S.StepEmptyState style={{ marginTop: '16px' }}>
            <S.Title>
              <span className="step">1</span>
              <h5 className="c-title">Informações cadastrais</h5>
            </S.Title>
          </S.StepEmptyState>
        )}

        {store.completedSteps.includes(1) ? (
          <S.CardInfo>
            <div className="c-header">
              <S.Title completed={store.completedSteps.includes(1)}>
                <span className="step">2</span>
                <h5 className="c-title">Procedimentos e OPME</h5>
              </S.Title>
              <ButtonNew
                className="btn-edit"
                icon={<EditIcon />}
                variant="text"
                fontSize="xsmall"
                noPadding
                onClick={() => handleEditButton(OrderStepsPath.PROCEDURE)}
              >
                Editar
              </ButtonNew>
            </div>
            <div className="c-content">
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Procedimentos adicionados</h6>
                </div>
                <div className="box-content">
                  {procedureGroups.hasVisible ? (
                    <S.TableContainer>
                      <S.Table>
                        <thead>
                          <tr>
                            <th>Procedimento</th>
                            <th className="col-code">Código</th>
                            <th className="col-quant">Quant.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {procedureGroups.template.length > 0 && (
                            <>
                              <tr className="group-header-row">
                                <td colSpan={3} className="group-header">
                                  Gabarito
                                </td>
                              </tr>
                              {procedureGroups.template.map((proc) => (
                                <tr key={proc.code}>
                                  <td>{proc.description}</td>
                                  <td>{proc.code}</td>
                                  <td>
                                    {String(proc.quantity).padStart(2, '0')}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}

                          {procedureGroups.doctor.length > 0 && (
                            <>
                              <tr className="group-header-row">
                                <td colSpan={3} className="group-header">
                                  Inclusões do médico
                                </td>
                              </tr>
                              {procedureGroups.doctor.map((proc) => (
                                <tr key={proc.code}>
                                  <td>{proc.description}</td>
                                  <td>{proc.code}</td>
                                  <td>
                                    {String(proc.quantity).padStart(2, '0')}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </S.Table>
                    </S.TableContainer>
                  ) : (
                    <p className="text">-</p>
                  )}
                </div>
              </S.BoxInfo>

              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">CIDs adicionados</h6>
                </div>
                <div className="box-content">
                  {store.cids.length > 0 ? (
                    <S.TableContainer>
                      <S.Table>
                        <thead>
                          <tr>
                            <th>Descrição</th>
                            <th className="col-code">Código</th>
                          </tr>
                        </thead>
                        <tbody>
                          {store.cids.map((cid) => (
                            <tr key={cid.code}>
                              <td>{cid.description}</td>
                              <td>{cid.code}</td>
                            </tr>
                          ))}
                        </tbody>
                      </S.Table>
                    </S.TableContainer>
                  ) : (
                    <p className="text">-</p>
                  )}
                </div>
              </S.BoxInfo>

              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">OPMEs adicionados</h6>
                </div>
                <div className="box-content">
                  {opmeGroups.hasVisible ? (
                    <S.TableContainer>
                      <S.Table>
                        <thead>
                          <tr>
                            <th>OPME</th>
                            <th className="col-quant">Quant.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {opmeGroups.template.length > 0 && (
                            <>
                              <tr className="group-header-row">
                                <td colSpan={2} className="group-header">
                                  Gabarito
                                </td>
                              </tr>
                              {opmeGroups.template.map((opme, index) => {
                                const lastLowAcceptanceIndex =
                                  opmeGroups.template.reduce(
                                    (lastIndex, opme, index) =>
                                      opme.low_acceptance ? index : lastIndex,
                                    -1
                                  )
                                const isLastLowAcceptance =
                                  opme.low_acceptance &&
                                  lastLowAcceptanceIndex === index

                                return (
                                  <React.Fragment key={opme.code}>
                                    <tr>
                                      <td>
                                        {opme.description}
                                        {opme.low_acceptance && (
                                          <S.LowAcceptanceTag>
                                            <AlertCircleIcon />
                                            <SupportTextNew
                                              as="span"
                                              size="xxsmall"
                                              color="danger900"
                                            >
                                              OPME negado em solicitações
                                              anteriores
                                            </SupportTextNew>
                                          </S.LowAcceptanceTag>
                                        )}
                                      </td>
                                      <td>
                                        {String(opme.quantity).padStart(2, '0')}
                                      </td>
                                    </tr>
                                    {isLastLowAcceptance &&
                                      !!store.opmeJustification && (
                                        <tr>
                                          <td
                                            colSpan={2}
                                            className="justification-row"
                                          >
                                            <p className="label">
                                              Para incluir OPMEs que já foram
                                              negados insira sua justificativa*
                                            </p>
                                            <div className="text-box">
                                              <p className="desc">
                                                {store.opmeJustification}
                                              </p>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                  </React.Fragment>
                                )
                              })}
                            </>
                          )}

                          {opmeGroups.doctor.length > 0 && (
                            <>
                              <tr className="group-header-row">
                                <td colSpan={2} className="group-header">
                                  Inclusões do médico
                                </td>
                              </tr>
                              {opmeGroups.doctor.map((opme) => (
                                <tr key={opme.code}>
                                  <td>
                                    {opme.description}
                                    {opme.low_acceptance && (
                                      <S.LowAcceptanceTag>
                                        <AlertCircleIcon />
                                        <SupportTextNew
                                          as="span"
                                          size="xxxsmall"
                                          color="danger800"
                                        >
                                          OPME negado em solicitações anteriores
                                        </SupportTextNew>
                                      </S.LowAcceptanceTag>
                                    )}
                                  </td>
                                  <td>
                                    {String(opme.quantity).padStart(2, '0')}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </S.Table>
                    </S.TableContainer>
                  ) : (
                    <p className="text">-</p>
                  )}
                </div>
              </S.BoxInfo>

              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Fornecedores indicados</h6>
                </div>
                <div className="box-content">
                  {providerGroups.hasVisible ? (
                    <S.TableContainer>
                      <S.Table>
                        <thead>
                          <tr>
                            <th>Fornecedor</th>
                            <th>Justificativa</th>
                          </tr>
                        </thead>
                        <tbody>
                          {providerGroups.template.length > 0 && (
                            <>
                              <tr className="group-header-row">
                                <td colSpan={2} className="group-header">
                                  Histórico de fornecedores aprovados
                                </td>
                              </tr>
                              {providerGroups.template.map((provider) => (
                                <tr key={provider.code}>
                                  <td>{provider.name}</td>
                                  <td>{provider.reason || '-'}</td>
                                </tr>
                              ))}
                            </>
                          )}

                          {providerGroups.doctor.length > 0 && (
                            <>
                              <tr className="group-header-row">
                                <td colSpan={2} className="group-header">
                                  Inclusões do médico
                                </td>
                              </tr>
                              {providerGroups.doctor.map((provider) => (
                                <tr key={provider.code}>
                                  <td>{provider.name}</td>
                                  <td>{provider.reason || '-'}</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </S.Table>
                    </S.TableContainer>
                  ) : (
                    <p className="text">-</p>
                  )}
                </div>
              </S.BoxInfo>
            </div>
          </S.CardInfo>
        ) : (
          <S.StepEmptyState>
            <S.SpacingLine />
            <S.Title>
              <span className="step">2</span>
              <h5 className="c-title">Procedimentos e OPME</h5>
            </S.Title>
          </S.StepEmptyState>
        )}

        {store.completedSteps.includes(2) ? (
          <S.CardInfo>
            <div className="c-header">
              <S.Title completed={store.completedSteps.includes(2)}>
                <span className="step">3</span>
                <h5 className="c-title">História e ficha clínica</h5>
              </S.Title>
              <ButtonNew
                className="btn-edit"
                icon={<EditIcon />}
                variant="text"
                fontSize="xsmall"
                noPadding
                onClick={() =>
                  handleEditButton(OrderStepsPath.CLINICAL_HISTORY)
                }
              >
                Editar
              </ButtonNew>
            </div>
            <div className="c-content">
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">História clínica</h6>
                </div>
                <div className="box-content -spacing">
                  <div>
                    <p className="label">
                      Descreva a história clínica do paciente
                    </p>
                    <div className="text-box">
                      <p className="desc">
                        {store.clinicalHistory.clinicalHistoryDescription ||
                          '-'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="label">
                      Descreva aspectos psicológicos, espirituais, culturais e
                      socioeconômicos a serem considerados:
                    </p>
                    <div className="text-box">
                      <p className="desc">
                        {store.clinicalHistory.clinicalHistoryAspects || '-'}
                      </p>
                    </div>
                  </div>
                  <Grid container spacing={1}>
                    <S.StyledGridItem item xs={12} sm={4} md={6}>
                      <p className="label">Restrição a transfusão sanguínea?</p>
                      <div className="text-box">
                        <p className="desc">
                          {booleansLabel(
                            store.clinicalHistory
                              .clinicalHistoryBloodTransfusion
                          )}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={4} md={6}>
                      <p className="label">Paciente oncológico?</p>
                      <div className="text-box">
                        <p className="desc">
                          {booleansLabel(
                            store.clinicalHistory.clinicalHistoryOncology
                          )}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={4} md={12}>
                      <p className="label">Paciente possui alergia a látex?</p>
                      <div className="text-box">
                        <p className="desc">
                          {store.clinicalHistory.clinicalHistoryLatexAllergy ||
                            '-'}
                        </p>
                      </div>
                    </S.StyledGridItem>
                  </Grid>
                  <div>
                    <p className="label">Paciente possui outra alergia?</p>
                    <div className="text-box">
                      <p className="desc">
                        {store.clinicalHistory.clinicalHistoryOtherAllergy ||
                          '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </S.BoxInfo>
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Ficha clínica</h6>
                </div>
                <div className="box-content">
                  <Grid container spacing={1}>
                    <S.StyledGridItem item xs={12} sm={6} md={6} xl={3}>
                      <p className="label">Técnica</p>
                      <div className="text-box">
                        <p className="desc">
                          {techniquesList.find(
                            (item) =>
                              item.value ===
                              store.clinicalHistory.clinicalHistoryTechnique
                          )?.label || '-'}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={6} md={6} xl={3}>
                      <p className="label">Congelação</p>
                      <div className="text-box">
                        <p className="desc">
                          {booleansLabel(
                            store.clinicalHistory.clinicalHistoryCongelation
                          )}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={6} md={6} xl={3}>
                      <p className="label">Reserva de sangue</p>
                      <div className="text-box">
                        <p className="desc">
                          {booleansLabel(
                            store.clinicalHistory.clinicalHistoryBloodReserve
                          )}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={6} md={6} xl={3}>
                      <p className="label">CTI</p>
                      <div className="text-box">
                        <p className="desc">
                          {booleansLabel(
                            store.clinicalHistory.clinicalHistoryCti
                          )}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={6}>
                      <p className="label">Tipo de diária</p>
                      <div className="text-box">
                        <p className="desc">
                          {hospitalizationModeList.find(
                            (item) =>
                              item.value ===
                              store.clinicalHistory
                                .clinicalHistoryHospitalizationMode
                          )?.label || '-'}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={6}>
                      <p className="label">Quantidade</p>
                      <div className="text-box">
                        <p className="desc">
                          {store.clinicalHistory
                            .clinicalHistoryHospitalizationDailyQuantity
                            ? `${store.clinicalHistory.clinicalHistoryHospitalizationDailyQuantity} diária(s)`
                            : '-'}{' '}
                        </p>
                      </div>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={6} xl={6}>
                      <S.StyledGridItem item container spacing={1}>
                        <S.StyledGridItem item xs={12} sm={6} md={12} xl={6}>
                          <p className="label">Caráter</p>
                          <div className="text-box">
                            <p className="desc">
                              {hospitalizationTypeList.find(
                                (item) =>
                                  item.value ===
                                  store.clinicalHistory
                                    .clinicalHistoryHospitalizationType
                              )?.label || '-'}
                            </p>
                          </div>
                        </S.StyledGridItem>
                        <S.StyledGridItem item xs={12} sm={6} md={12} xl={6}>
                          <p className="label">
                            {store.clinicalHistory
                              ?.clinicalHistoryHospitalizationType === 'urgency'
                              ? 'Data da urgência'
                              : 'Data prevista'}
                          </p>
                          <div className="text-box">
                            <p className="desc">
                              {store.clinicalHistory
                                ?.clinicalHistoryExpectedDate &&
                              moment(
                                store.clinicalHistory
                                  .clinicalHistoryExpectedDate
                              ).isValid()
                                ? moment(
                                    store.clinicalHistory
                                      .clinicalHistoryExpectedDate
                                  )
                                    .utc()
                                    .format('DD/MM/YYYY')
                                : '-'}
                            </p>
                          </div>
                        </S.StyledGridItem>
                        {!!store.clinicalHistory
                          .clinicalHistoryHospitalizationType && (
                          <S.StyledGridItem item xs={12}>
                            <div className="text-box -warning">
                              <p className="desc">
                                {store.clinicalHistory
                                  .clinicalHistoryHospitalizationType ===
                                'urgency'
                                  ? '**Declaro estar ciente de que a senha será solicitada com o paciente no local. Após 7 dias se procedimento não realizado o pedido será automaticamente cancelado.'
                                  : '**Declaro estar ciente de que este pedido será enviado à operadora e o prazo de resposta é de até 21 dias.'}
                              </p>
                            </div>
                          </S.StyledGridItem>
                        )}
                      </S.StyledGridItem>
                    </S.StyledGridItem>
                    <S.StyledGridItem item xs={12} sm={6} xl={6}>
                      <S.StyledGridItem item container spacing={1}>
                        <S.StyledGridItem item xs={12} sm={6} md={12} xl={6}>
                          <p className="label">Paciente internado</p>
                          <div className="text-box">
                            <p className="desc">
                              {booleansLabel(
                                store.patient.internation?.paciente_internado
                              )}
                            </p>
                          </div>
                        </S.StyledGridItem>
                        <S.StyledGridItem item xs={12} sm={6} md={12} xl={6}>
                          <p className="label">Previsão de alta</p>
                          <div className="text-box">
                            <p className="desc">
                              {store.clinicalHistory
                                ?.clinicalHistoryBullishForecastDate &&
                              moment(
                                store.clinicalHistory
                                  .clinicalHistoryBullishForecastDate
                              ).isValid()
                                ? moment(
                                    store.clinicalHistory
                                      .clinicalHistoryBullishForecastDate
                                  )
                                    .utc()
                                    .format('DD/MM/YYYY')
                                : '-'}
                            </p>
                          </div>
                        </S.StyledGridItem>
                        {store.patient.internation?.paciente_internado && (
                          <S.StyledGridItem item xs={12} md={12}>
                            <S.CardHospitalizedInfo>
                              <div>
                                <SupportTextNew
                                  color="info950"
                                  size="xxxsmall"
                                  weight="semiBold"
                                  fontFamily="secondary"
                                >
                                  Nº atendimento:
                                </SupportTextNew>
                                <SupportTextNew
                                  size="xxsmall"
                                  color="textPrimary"
                                  weight="semiBold"
                                >
                                  {store.patient.internation?.cd_atendimento
                                    ? store.patient.internation?.cd_atendimento
                                    : '-'}
                                </SupportTextNew>
                              </div>
                              <div>
                                <SupportTextNew
                                  color="info950"
                                  size="xxxsmall"
                                  weight="semiBold"
                                  fontFamily="secondary"
                                >
                                  Local Internação:
                                </SupportTextNew>
                                <SupportTextNew
                                  size="xxsmall"
                                  color="textPrimary"
                                  weight="semiBold"
                                >
                                  {store.patient.internation?.ds_leito
                                    ? store.patient.internation?.ds_leito
                                    : '-'}
                                </SupportTextNew>
                              </div>
                            </S.CardHospitalizedInfo>
                          </S.StyledGridItem>
                        )}
                      </S.StyledGridItem>
                    </S.StyledGridItem>
                  </Grid>

                  <div>
                    <p className="label">Observações médicas</p>
                    <div className="text-box">
                      <p className="desc">
                        {store.clinicalHistory
                          .clinicalHistoryMedicalObservations || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </S.BoxInfo>
            </div>
          </S.CardInfo>
        ) : (
          <S.StepEmptyState>
            <S.SpacingLine />
            <S.Title>
              <span className="step">3</span>
              <h5 className="c-title">História e ficha clínica</h5>
            </S.Title>
          </S.StepEmptyState>
        )}

        {store.completedSteps.includes(3) ||
        !!store.surgicalOrder?.documents?.length ||
        !!store.surgicalOrder?.reportRecommendation ? (
          <S.CardInfo>
            <div className="c-header">
              <S.Title
                completed={
                  store.completedSteps.includes(3) ||
                  !!store.surgicalOrder?.documents?.length ||
                  !!store.surgicalOrder?.reportRecommendation
                }
              >
                <span className="step">4</span>
                <h5 className="c-title">Laudos e documentos</h5>
              </S.Title>
              <ButtonNew
                className="btn-edit"
                icon={<EditIcon />}
                variant="text"
                fontSize="xsmall"
                noPadding
                onClick={() =>
                  handleEditButton(OrderStepsPath.REPORTS_AND_DOCUMENTS)
                }
              >
                Editar
              </ButtonNew>
            </div>
            <div className="c-content">
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Laudos</h6>
                </div>
                <div className="box-content">
                  {filteredReports.map(
                    (document, index) =>
                      index === 0 && (
                        <ReportsCard
                          key={document.type}
                          documents={filteredReports}
                          downloadDocument={downloadDocument}
                          showStatus={false}
                        />
                      )
                  )}
                  {/* TODO: Campo indicar laudo do sistema do hospital */}
                  {store.surgicalOrder.reportRecommendation && (
                    <p>{store.surgicalOrder.reportRecommendation}</p>
                  )}
                </div>
              </S.BoxInfo>
              <S.BoxInfo>
                <div className="box-header">
                  <h6 className="box-title">Outros documentos</h6>
                </div>
                <div className="box-content">
                  {documentsList()
                    .filter(
                      (document) => !documentsNotToShow.includes(document.type)
                    )
                    .map((document) => (
                      <DocumentAccordeon
                        gap="8px"
                        key={document.type}
                        document={document}
                        shadow="satin"
                        padding="16px"
                        onDownloadFile={downloadDocument}
                        expandedDocument={informationAccordionOpen}
                        handleChange={handleChangeInformationAccordion}
                        uploadButton={true}
                        showStatus={false}
                        reportsRecommendations={
                          store.surgicalOrder?.reportRecommendation
                        }
                      />
                    ))}
                </div>
              </S.BoxInfo>
            </div>
          </S.CardInfo>
        ) : (
          <S.StepEmptyState>
            <S.SpacingLine />
            <S.Title>
              <span className="step">4</span>
              <h5 className="c-title">Laudos e documentos</h5>
            </S.Title>
          </S.StepEmptyState>
        )}
      </S.Wrapper>
    )
  })
)

export default ReviewSurgicalOrder
